import { usePitcher } from "features/utils/usePitcher";
import type { ResourceAlert } from "hooks/pitcher/useVMResourceAlert";
import { useVMResourceAlert } from "hooks/pitcher/useVMResourceAlert";
import { ButtonBadge } from "prism-react";
import { useEffect, useRef, useState } from "react";

import { useAnalytics } from "hooks/useAnalytics";
import { executeVSCodeCommand } from "utils/vscode";

const SECONDS_PASSED_UNTIL_VM_SPECS_ARE_LIKELY_READY = 30_000;

export const VMResourcesStatusBadge: React.FC = () => {
  const pitcher = usePitcher();
  const resourceAlert = useVMResourceAlert();

  // We do not want to show the badge until some time has passed. The reason is that the VM
  // might be adjusting its specs (Memory Balooning), so we avoid showing the badge until
  // we know it is likely to be done adjusting
  const [hasLikelyAdjustedVMSpecs, setHasLikelyAdjustedVMSpecs] = useState(
    () =>
      Date.now() - pitcher.sessionStartTime >
      SECONDS_PASSED_UNTIL_VM_SPECS_ARE_LIKELY_READY,
  );
  const track = useAnalytics();
  const showBadge = resourceAlert && hasLikelyAdjustedVMSpecs;
  const showBadgeRef = useRef(showBadge);

  useEffect(() => {
    // Everytime the badge is shown, but was not shown
    // at the previous render, track the event
    if (!showBadgeRef.current && showBadge) {
      track("show_resource_alert", { type: resourceAlert.type });
    }

    showBadgeRef.current = showBadge;
  }, [showBadge]);

  // If we have not yet evaluated VM specs we want to make sure to show the badge, even though there has been no VM spec updates in the
  // meantime
  useEffect(() => {
    if (!hasLikelyAdjustedVMSpecs) {
      const timeout = setTimeout(
        () => {
          setHasLikelyAdjustedVMSpecs(true);
        },
        pitcher.sessionStartTime +
          SECONDS_PASSED_UNTIL_VM_SPECS_ARE_LIKELY_READY -
          Date.now(),
      );

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [hasLikelyAdjustedVMSpecs]);

  if (!showBadge) {
    return null;
  }

  return (
    <ButtonBadge
      onClick={() => {
        track("open_system_metrics", { event_source: "badge" });
        executeVSCodeCommand("codesandbox.openCsbTab", {
          type: "VIRTUAL_MACHINE",
        });
      }}
      title="See detailed VM resource usage"
      variant={resourceAlert.severity}
    >
      {`${METRIC_LABEL[resourceAlert.type]} ${resourceAlert.value}%`}
    </ButtonBadge>
  );
};

const METRIC_LABEL: Record<ResourceAlert["type"], string> = {
  cpu: "CPU usage",
  memory: "RAM usage",
  storage: "Disk usage",
};
